/**
 * useFormBuilder Hooks
 *
 * React Query hooks for the Form Builder CRUD operations.
 */

import { queryKeys } from '@/lib/query-client';
import type { FormTransferPackage } from '@skewb-works/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createForm,
  deleteForm,
  getControlConfigs,
  getFormById,
  getFormList,
  getFormTypes,
  updateForm,
} from '../api/formApi';
import { exportFormPackage, importFormPackage } from '../api/formTransferApi';
import type { FormDefinition } from '../components/form-builder/types';

/** Extend query keys for forms */
const formKeys = {
  all: [...queryKeys.settings.all, 'forms'] as const,
  lists: () => [...formKeys.all, 'list'] as const,
  detail: (id: number) => [...formKeys.all, 'detail', id] as const,
  configs: () => [...formKeys.all, 'configs'] as const,
  types: () => [...formKeys.all, 'types'] as const,
};

export function useFormList() {
  return useQuery({
    queryKey: formKeys.lists(),
    queryFn: getFormList,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

export function useForm(id: number | null) {
  return useQuery({
    queryKey: formKeys.detail(id ?? 0),
    queryFn: () => getFormById(id!),
    enabled: id !== null && id > 0,
    staleTime: 30 * 1000,
  });
}

export function useCreateForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: FormDefinition) => createForm(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formKeys.lists() });
    },
    meta: {
      resourceName: 'Form',
      action: 'create',
    },
  });
}

export function useUpdateForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: FormDefinition }) => updateForm(id, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: formKeys.lists() });
      queryClient.invalidateQueries({ queryKey: formKeys.detail(variables.id) });
    },
    meta: {
      resourceName: 'Form',
      action: 'update',
    },
  });
}

export function useDeleteForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteForm(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: formKeys.lists() });
    },
    meta: {
      resourceName: 'Form',
      action: 'delete',
    },
  });
}

export function useControlConfigs() {
  return useQuery({
    queryKey: formKeys.configs(),
    queryFn: getControlConfigs,
    staleTime: 10 * 60 * 1000,
  });
}

export function useFormTypes() {
  return useQuery({
    queryKey: formKeys.types(),
    queryFn: getFormTypes,
    staleTime: 10 * 60 * 1000,
  });
}

export function useExportForm() {
  return useMutation({
    mutationFn: (id: number) => exportFormPackage(id),
    meta: { skipSuccessToast: true, errorTitle: 'Unable to export form' },
  });
}

export function useImportForm() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (pkg: FormTransferPackage) => importFormPackage(pkg),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: formKeys.lists() });
      if (result.form_type_created) {
        queryClient.invalidateQueries({ queryKey: formKeys.types() });
      }
    },
    meta: { skipSuccessToast: true, errorTitle: 'Unable to import form' },
  });
}
